import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { cn } from '@/lib/utils';

import { bookingTime, minutesFromMidnight } from '@/helpers';

type SlotPickerProps = {
  label: string;
  slots: { starts_at: string }[];
  value: string | null;
  isLoading: boolean;
  placeholder?: ReactNode;
  isInvalid?: boolean;
  onChange: (startsAt: string) => void;
};

export const SlotPicker = (props: SlotPickerProps) => {
  const { label, slots, value, isLoading, placeholder, isInvalid, onChange } =
    props;
  const { t } = useTranslation();

  // A clicked calendar cell gives a bare local timestamp, the slots a zoned one — match the clock.
  const selectedMinute = value ? minutesFromMidnight(value) : null;

  return (
    <div className="relative">
      <fieldset
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-0 top-[-5px] overflow-hidden rounded-md border-2 px-[9px]',
          isInvalid ? 'border-destructive' : 'border-input',
        )}
      >
        <legend className="invisible block h-[11px] w-auto max-w-full overflow-hidden p-0 text-xs whitespace-nowrap">
          <span className="visible inline-block px-1 opacity-0">{label}</span>
        </legend>
      </fieldset>
      <span
        className={cn(
          'pointer-events-none absolute top-0 left-[13px] -translate-y-1/2 text-xs',
          isInvalid ? 'text-destructive' : 'text-muted-foreground',
        )}
      >
        {label}
      </span>

      <div className="p-3">
        {isLoading && <Skeleton className="h-8 w-full" />}

        {!isLoading && !!placeholder && (
          <p className="text-muted-foreground text-sm">{placeholder}</p>
        )}

        {!isLoading && !placeholder && slots.length === 0 && (
          <p className="text-muted-foreground text-sm">
            {t('No free slots for this date')}
          </p>
        )}

        {!isLoading && !placeholder && slots.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {slots.map((slot) => (
              <Button
                key={slot.starts_at}
                type="button"
                variant={
                  selectedMinute === minutesFromMidnight(slot.starts_at)
                    ? 'default'
                    : 'outline'
                }
                size="sm"
                className="min-w-16"
                onClick={() => onChange(slot.starts_at)}
              >
                {bookingTime(slot.starts_at)}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
